import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  TextField,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Services from 'src/services/Services';

function normalizeFaqType(type) {
  const t = String(type || '').toLowerCase();
  return t === 'porter' ? 'porter' : 'store';
}

function normalizeSource(source) {
  const s = String(source || '').toLowerCase();
  return s === 'qr' ? 'qr' : '';
}

function renderTextWithLinks(text) {
  if (!text) return null;

  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      return (
        <Link
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{ wordBreak: 'break-all' }}
        >
          {part}
        </Link>
      );
    }

    return (
      <Typography
        key={index}
        component="span"
        variant="body2"
        sx={{ whiteSpace: 'pre-line' }}
      >
        {part}
      </Typography>
    );
  });
}

export default function FaqWithoutLogin() {
  const navigate = useNavigate();
  const { type, source } = useParams();

  const faqType = useMemo(() => normalizeFaqType(type), [type]);
  const sourceParam = useMemo(() => normalizeSource(source), [source]);

  const [searchText, setSearchText] = useState('');
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState('');

  const logSentRef = useRef(false);
  const lastLogKeyRef = useRef('');

  useEffect(() => {
    if (type == null) return;

    const t = String(type).toLowerCase();
    if (t !== 'porter' && t !== 'store') {
      if (sourceParam) {
        navigate('/sss/store/qr', { replace: true });
      } else {
        navigate('/sss/store', { replace: true });
      }
    }
  }, [type, navigate, sourceParam]);

  const sourceValue = useMemo(() => {
    return sourceParam === 'qr' ? 'qr' : 'url';
  }, [sourceParam]);

  useEffect(() => {
    const key = `${faqType}__${sourceValue}`;
    if (lastLogKeyRef.current === key && logSentRef.current) return;

    lastLogKeyRef.current = key;
    logSentRef.current = true;

    Services.CreateFaqLog(sourceValue, faqType).catch(() => {});
  }, [faqType, sourceValue]);

  // FAQ çek
  useEffect(() => {
    let mounted = true;

    setLoading(true);
    setErrorText('');

    Services.GetFaqWithoutToken(faqType)
      .then((res) => {
        const ok = res?.data?.status;
        const list = res?.data?.result || [];
        if (!mounted) return;

        if (ok) {
          setAllData(list);
          setFilteredData(list);
        } else {
          setAllData([]);
          setFilteredData([]);
          setErrorText(res?.data?.message || 'SSS bulunamadı.');
        }
      })
      .catch(() => {
        if (!mounted) return;
        setAllData([]);
        setFilteredData([]);
        setErrorText('SSS çekilirken hata oluştu.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [faqType]);

  // Arama filtresi
  useEffect(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) {
      setFilteredData(allData);
      return;
    }

    const next = allData.filter((item) => {
      const title = (item?.title || '').toLowerCase();
      const desc = (item?.description || '').toLowerCase();
      return title.includes(q) || desc.includes(q);
    });

    setFilteredData(next);
  }, [searchText, allData]);

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 6 } }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h4">
            SSS – {faqType === 'porter' ? 'Porter' : 'Mağaza'}
          </Typography>
        </Box>

        <Divider />

        <TextField
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Ara..."
          fullWidth
        />

        {errorText && <Alert severity="error">{errorText}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {filteredData?.length === 0 ? (
              <Alert severity="info">Gösterilecek SSS bulunamadı.</Alert>
            ) : (
              filteredData?.map((item) => (
                <Accordion key={String(item?.id ?? item?.title)}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1">{item?.title}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {renderTextWithLinks(item?.description)}
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>
        )}
      </Stack>
    </Container>
  );
}
